/**
 * CalendarMonthGrid — calm month view for admin/parent calendar.
 *
 * Renders a 6-row × 7-column grid of the current month. Events are bucketed
 * by date and shown as small calm chips with their kind color. Clicking a
 * day surfaces a side-panel of events for that day; clicking a chip calls
 * `onPick(event)` so the parent can route to an edit composer or detail view.
 *
 * Pure visual layer — does not own state, takes events + a date range.
 */
import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function bucketize(events) {
  const out = {};
  for (const e of events) {
    const day = (e.starts_at || "").slice(0, 10);
    if (!day) continue;
    if (!out[day]) out[day] = [];
    out[day].push(e);
  }
  return out;
}

export default function CalendarMonthGrid({
  events,
  initialMonth,
  kindColors = {},
  onPick,
}) {
  const [cursor, setCursor] = useState(() => initialMonth || new Date());

  const grid = useMemo(() => {
    const first = startOfMonth(cursor);
    const last = endOfMonth(cursor);
    // Build grid starting from Monday of the week containing day 1.
    const firstDow = (first.getDay() + 6) % 7; // 0=Mon
    const start = new Date(first);
    start.setDate(start.getDate() - firstDow);
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push(d);
    }
    return { cells, first, last };
  }, [cursor]);

  const buckets = useMemo(() => bucketize(events), [events]);
  const monthLabel = cursor.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
  const today = ymd(new Date());

  const move = (delta) => {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1);
    setCursor(next);
  };

  return (
    <div data-testid="calendar-month-grid" className="rounded-3xl bg-white border hairline overflow-hidden">
      <header className="px-6 py-4 flex items-center justify-between border-b hairline">
        <button
          type="button"
          onClick={() => move(-1)}
          data-testid="cal-grid-prev"
          aria-label="Previous month"
          className="h-8 w-8 rounded-full bg-[#FAF6EE] hover:bg-[#F2EFEA] flex items-center justify-center text-[#525252]"
        >
          <ChevronLeft size={16} strokeWidth={1.6} />
        </button>
        <h3 className="font-serif text-xl text-[#171717]" data-testid="cal-grid-label">
          {monthLabel}
        </h3>
        <button
          type="button"
          onClick={() => move(1)}
          data-testid="cal-grid-next"
          aria-label="Next month"
          className="h-8 w-8 rounded-full bg-[#FAF6EE] hover:bg-[#F2EFEA] flex items-center justify-center text-[#525252]"
        >
          <ChevronRight size={16} strokeWidth={1.6} />
        </button>
      </header>

      <div className="grid grid-cols-7 text-[10px] tracking-[0.18em] uppercase text-[#525252] px-1 py-2">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="text-center">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-px bg-[#E6E1D6]">
        {grid.cells.map((d, i) => {
          const key = ymd(d);
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = key === today;
          const dayEvents = buckets[key] || [];
          return (
            <div
              key={i}
              data-testid={`cal-grid-cell-${key}`}
              className={`bg-white min-h-[100px] p-2 ${inMonth ? "" : "bg-[#FAF6EE]/50"} ${isToday ? "ring-2 ring-[#1F3A2A] ring-inset" : ""}`}
            >
              <div className={`text-xs font-mono ${inMonth ? "text-[#171717]" : "text-[#9CA3AF]"} ${isToday ? "font-bold text-[#1F3A2A]" : ""}`}>
                {d.getDate()}
              </div>
              <ul className="mt-1 space-y-1">
                {dayEvents.slice(0, 3).map((ev) => (
                  <li key={ev.id}>
                    <button
                      type="button"
                      onClick={() => onPick && onPick(ev)}
                      data-testid={`cal-grid-event-${ev.id}`}
                      className="w-full text-left text-[10px] truncate rounded px-1.5 py-0.5 hover:bg-[#FAF6EE]"
                      style={{
                        background: `${kindColors[ev.kind] || "#9BA995"}22`,
                        color: kindColors[ev.kind] || "#1F3A2A",
                      }}
                      title={ev.title}
                    >
                      {ev.title}
                    </button>
                  </li>
                ))}
                {dayEvents.length > 3 && (
                  <li className="text-[10px] text-[#525252] px-1.5">
                    +{dayEvents.length - 3} more
                  </li>
                )}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
